import { Box, HStack, VStack } from '@chakra-ui/react';
import { AppTracker } from 'shared/analytics/tracker';
import { useEffect } from 'react';
import {
  ANALYTIC_V3_EVENTS,
  RESPONSE_METRICS_OPTIONS_MAP,
} from 'views/main/organization/analytics-v3/constants';
import { useAnalyticsV3Context } from 'views/main/organization/analytics-v3/hooks/useAnalyticsV3Context';
import {
  useGetAnalyticsXTTAByPriorityExport,
  useGetAnalyticsXTTAVsTimeExport,
  useGetAnalyticsXTTRByPriorityExport,
  useGetAnalyticsXTTRVsTimeExport,
  useGetAnalyticsXTTAByServiceExport,
  useGetAnalyticsXTTRByServiceExport,
} from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import PanelCard from '../../../PanelCard';
import { XTTAByPriority } from './XTTAByPriority';
import { XTTAByService } from './XTTAByService';
import { XTTAVsTime } from './XTTAVsTime';
import { XTTRByPriority } from './XTTRByPriority';
import { XTTRByService } from './XTTRByService';
import { XTTRVsTime } from './XTTRVsTime';
import { Container } from './style';

function ResponseMetrics({ isOrgAnalytics = false }: { isOrgAnalytics?: boolean }) {
  const selectedMetric = useAnalyticsV3Context(state => state.responseMetrics.metricType);

  const selectedMetricTypeLabel = RESPONSE_METRICS_OPTIONS_MAP[selectedMetric];

  const { isLoading, refetch: refetchXTTAVsTime } = useGetAnalyticsXTTAVsTimeExport({
    isOrgAnalytics,
  });
  const { isLoading: isLoadingXTTRVsTime, refetch: refetchXTTRVsTime } =
    useGetAnalyticsXTTRVsTimeExport({ isOrgAnalytics });

  const { isLoading: isLoadingXTTAByPriorityExport, refetch: refetchXTTAByPriorityExport } =
    useGetAnalyticsXTTAByPriorityExport();
  const { isLoading: isLoadingXTTRByPriorityExport, refetch: refetchXTTRByPriorityExport } =
    useGetAnalyticsXTTRByPriorityExport();

  const { isLoading: isLoadingXTTAByServiceExport, refetch: refetchXTTAByServiceExport } =
    useGetAnalyticsXTTAByServiceExport();
  const { isLoading: isLoadingXTTRByServiceExport, refetch: refetchXTTRByServiceExport } =
    useGetAnalyticsXTTRByServiceExport();

  useEffect(() => {
    AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_PAGE_VIEWED, {
      'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
      'Analytics Type': 'Incident Analytics',
      'Analytics Tab': 'Response Metrics',
    });
  }, []);

  const handleExportClick = (chartTitle: string, exportFn: () => void) => {
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Response Metrics',
        'Chart Title': chartTitle,
        'Metric Type': selectedMetricTypeLabel,
      },
      true,
    );
    exportFn();
  };

  const isMeanSelected = selectedMetric === 'mean';

  return (
    <Container>
      <div className="row">
        <PanelCard
          title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
            isMeanSelected ? 'MTTA' : 'TTA'
          } over time`}
          onExportClick={() => handleExportClick('TTA over time', refetchXTTAVsTime)}
        >
          <Box position="relative" height={282} overflow="hidden">
            <XTTAVsTime
              selectedMetricTypeLabel={selectedMetricTypeLabel}
              isOrgAnalytics={isOrgAnalytics}
            />
          </Box>
        </PanelCard>
        <PanelCard
          title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
            isMeanSelected ? 'MTTR' : 'TTR'
          } over time`}
          onExportClick={() => handleExportClick('TTR over time', refetchXTTRVsTime)}
        >
          <Box position="relative" height={282} overflow="hidden" marginInlineStart={'0px'}>
            <XTTRVsTime
              selectedMetricTypeLabel={selectedMetricTypeLabel}
              isOrgAnalytics={isOrgAnalytics}
            />
          </Box>
        </PanelCard>
      </div>
      {!isOrgAnalytics && (
        <>
          <div className="row">
            <PanelCard
              title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
                isMeanSelected ? 'MTTA' : 'TTA'
              } by Priority`}
              onExportClick={() =>
                handleExportClick('TTA by Priority', refetchXTTAByPriorityExport)
              }
            >
              <Box position="relative" height={282} overflow="hidden">
                <XTTAByPriority />
              </Box>
            </PanelCard>
            <PanelCard
              title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
                isMeanSelected ? 'MTTR' : 'TTR'
              } by Priority`}
              onExportClick={() =>
                handleExportClick('TTR by Priority', refetchXTTRByPriorityExport)
              }
            >
              <Box position="relative" height={282} overflow="hidden">
                <XTTRByPriority />
              </Box>
            </PanelCard>
          </div>
          <div className="row">
            <PanelCard
              title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
                isMeanSelected ? 'MTTA' : 'TTA'
              } by Service`}
              onExportClick={() => handleExportClick('TTA by Service', refetchXTTAByServiceExport)}
              chartDescription="TOP 10 SERVICES"
            >
              <Box position="relative" height={616} overflow="hidden">
                <XTTAByService />
              </Box>
            </PanelCard>
            <PanelCard
              title={`${isMeanSelected ? '' : selectedMetricTypeLabel} ${
                isMeanSelected ? 'MTTR' : 'TTR'
              } by Service`}
              onExportClick={() => handleExportClick('TTR by Service', refetchXTTRByServiceExport)}
              chartDescription="TOP 10 SERVICES"
            >
              <Box position="relative" height={616} overflow="hidden">
                <XTTRByService />
              </Box>
            </PanelCard>
          </div>
        </>
      )}
    </Container>
  );
}

export default ResponseMetrics;
